import React from 'react';
import { Handle, Position } from 'reactflow';
import type { TurnMarkerData } from '../types';
import { useTheme } from '../../../../contexts/ThemeContext';
import { usePersonaChatUI } from '../../../../contexts/PersonaChatUIContext';
import { useDagNodeOpacity } from '../../../../contexts/ChatDagRuntimeContext';
import HoverCard from '../../../common/HoverCard';
import DagNodeHoverContent from './DagNodeHoverContent';
import { MarkdownPreview } from '../MarkdownPreview';
import { CopyChip } from '../CopyChip';
import {
  PIP_HEADER_CLASS,
  HOVER_LIFT_CLASS,
  SELECTION_RING_CLASS,
} from '../chatDagTokens';

// Anchor for the start/end of a turn.
//
// UX triad review (v0.2): the DIFFERENTIATING field is the content
// preview — every user marker has the same "👤 You" but different
// words. Render the preview as the dominant text; push the role label
// to a small pip so the eye lands on signal, not chrome.
//
// Assistant markers surface a compact telemetry chip row
// (🧰 · 💰 · tokens) pulled from `message.metadata.turn_telemetry`.

function formatCost(usd: number | null | undefined): string {
  if (usd == null) return '';
  if (usd === 0) return '$0';
  if (usd < 0.001) return `$${usd.toFixed(5)}`;
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}

// T3 — cost tier color so an expensive turn FEELS expensive on the
// asst marker: amber over 5¢, rose over $1. Returns '' for the
// default opacity-80 inherited tone.
function costTierClass(usd: number | null | undefined, darkMode: boolean): string {
  if (usd == null) return '';
  if (usd >= 1) return darkMode ? 'text-rose-400 font-semibold' : 'text-rose-600 font-semibold';
  if (usd >= 0.05) return darkMode ? 'text-amber-300' : 'text-amber-600';
  return '';
}

function formatTime(ts?: string): string {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export const TurnMarkerNodeCompact: React.FC<{ data: TurnMarkerData; selected?: boolean }> = ({ data, selected }) => {
  const { darkMode } = useTheme();
  const { focusBubble } = usePersonaChatUI();
  const opacityStyle = useDagNodeOpacity(data.turnKey, false);
  const isUser = data.role === 'user';
  const messageId = data.messageId;
  const onClick = messageId
    ? (e: React.MouseEvent) => {
        e.stopPropagation();
        focusBubble(messageId);
      }
    : undefined;

  // User markers: blue pennant reading "incoming intent."
  // Assistant markers: emerald keystone "turn closes here."
  const accent = isUser
    ? darkMode
      ? 'bg-blue-600/25 border-blue-500/70 text-blue-50'
      : 'bg-blue-50 border-blue-400 text-blue-900'
    : darkMode
      ? 'bg-emerald-700/25 border-emerald-500/70 text-emerald-50'
      : 'bg-emerald-50 border-emerald-500 text-emerald-900';

  const pipStyle = isUser
    ? darkMode
      ? 'text-blue-300/70'
      : 'text-blue-600'
    : darkMode
      ? 'text-emerald-300/70'
      : 'text-emerald-600';

  return (
    <HoverCard
      maxWidth={360}
      enterDelay={250}
      content={<DagNodeHoverContent data={data} />}
    >
    <div
      className={`group relative px-3 py-2 rounded-md border text-xs w-[260px] shadow-sm ${HOVER_LIFT_CLASS} ${accent} ${
        selected ? SELECTION_RING_CLASS : ''
      } ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      title={onClick ? `${data.preview}\n\nClick to scroll to bubble` : data.preview}
      // Soft visual distinction per role — asymmetric corner
      // rounding, so a user marker points "down into" the flow and
      // an assistant marker caps "up from" the fan-in.
      // Dim non-viewport turns per the spec rev 1.1 § 9 priority rules.
      style={{
        ...(isUser
          ? { borderTopLeftRadius: 12, borderBottomRightRadius: 12 }
          : { borderTopRightRadius: 12, borderBottomLeftRadius: 12 }),
        ...opacityStyle,
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="t"
        className="!opacity-0 !pointer-events-none"
      />

      {(data.fullContent || data.preview) && (
        <CopyChip
          text={data.fullContent || data.preview}
          label={isUser ? 'Copy prompt' : 'Copy answer'}
        />
      )}

      {/* Tiny role + time pip — not the main event */}
      <div className={`flex items-center gap-1.5 ${PIP_HEADER_CLASS} ${pipStyle}`}>
        <span>{isUser ? '👤 You' : '🤖 Assistant'}</span>
        {data.timestamp && <span className="opacity-60 font-mono normal-case tracking-normal">{formatTime(data.timestamp)}</span>}
      </div>

      {/* Preview — the load-bearing content of this node.
          Asst markers carry LLM markdown source; render through
          MarkdownPreview so **bold**, lists, inline code etc. read
          as styled text instead of raw asterisks. User markers stay
          plain — typed prose rarely contains markdown. */}
      <div className="mt-1 font-medium leading-snug">
        {data.preview ? (
          isUser ? (
            <div className="line-clamp-3 whitespace-pre-wrap break-words">
              {data.preview}
            </div>
          ) : (
            <MarkdownPreview
              text={data.preview}
              maxLines={3}
              textSizeClass="text-xs"
            />
          )
        ) : (
          <span className="opacity-50 italic font-normal">(empty turn)</span>
        )}
      </div>

      {/* Assistant-only telemetry chips */}
      {!isUser && (data.totalCostUsd != null || data.toolCallCount || data.totalTokens != null) ? (
        <div className="mt-1.5 flex items-center gap-2.5 text-[10px] font-mono opacity-80 pt-1.5 border-t border-current/10">
          {data.toolCallCount ? <span title="Tool invocations this turn">🧰 {data.toolCallCount}</span> : null}
          {data.totalCostUsd != null ? <span title="Estimated cost" className={costTierClass(data.totalCostUsd, darkMode)}>💰 {formatCost(data.totalCostUsd)}</span> : null}
          {data.totalTokens != null ? <span title="Total tokens">{data.totalTokens.toLocaleString()} tok</span> : null}
        </div>
      ) : null}

      <Handle
        type="source"
        position={Position.Bottom}
        id="b"
        className="!opacity-0 !pointer-events-none"
      />
    </div>
    </HoverCard>
  );
};

export default TurnMarkerNodeCompact;
